import type { WallCard } from '@walls/store';
import { getBoardCardTree } from '../selectors/boardCardSelectors';
import { autoAlignGap } from './autoAlign';
import { getPositionedCardDimensions } from './cardRects';

export interface OrderPageCardsBoundary {
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface OrderPageCardsOptions {
  gap?: number;
  measuredRects?: Record<string, Rect>;
}

export interface OrderedPageCardPosition {
  cardId: string;
  xPos: number;
  yPos: number;
}

interface Rect {
  height: number;
  width: number;
  x: number;
  y: number;
}

const defaultGap = autoAlignGap;
const maxScanAttempts = 10000;

export function getOrderPageCardPositionUpdates(
  cards: WallCard[],
  boundary: OrderPageCardsBoundary,
  options: OrderPageCardsOptions = {}
): OrderedPageCardPosition[] {
  const gap = options.gap ?? defaultGap;
  const rootElements = getRootPageElements(cards);
  const lockedRects = rootElements
    .filter((card) => card.isLocked)
    .map((card) => getCardRect(card, options.measuredRects));
  const occupiedRects = [...lockedRects];
  const unlockedElements = rootElements.filter((card) => !card.isLocked).sort(sortCardsByPosition);

  return unlockedElements.map((card) => {
    const dimensions = getOrderCardDimensions(card, options.measuredRects);
    const position = findNextAvailablePosition(dimensions, occupiedRects, boundary, gap);

    occupiedRects.push({
      ...position,
      ...dimensions,
    });

    return {
      cardId: card.id,
      xPos: position.x,
      yPos: position.y,
    };
  });
}

function getRootPageElements(cards: WallCard[]) {
  const { rootCards, rootClusters } = getBoardCardTree(cards);

  return [...rootCards, ...rootClusters];
}

function sortCardsByPosition(left: WallCard, right: WallCard) {
  return (
    (left.yPos ?? 0) - (right.yPos ?? 0) ||
    (left.xPos ?? 0) - (right.xPos ?? 0) ||
    (left.zPos ?? 0) - (right.zPos ?? 0) ||
    left.id.localeCompare(right.id)
  );
}

function getCardRect(card: WallCard, measuredRects?: Record<string, Rect>): Rect {
  const measuredRect = measuredRects?.[card.id];
  const dimensions = getOrderCardDimensions(card, measuredRects);

  return {
    x: measuredRect?.x ?? card.xPos ?? 0,
    y: measuredRect?.y ?? card.yPos ?? 0,
    ...dimensions,
  };
}

function getOrderCardDimensions(card: WallCard, measuredRects?: Record<string, Rect>) {
  const measuredRect = measuredRects?.[card.id];

  if (measuredRect) {
    return {
      height: measuredRect.height,
      width: measuredRect.width,
    };
  }

  return getPositionedCardDimensions(card);
}

function findNextAvailablePosition(
  dimensions: { height: number; width: number },
  occupiedRects: Rect[],
  boundary: OrderPageCardsBoundary,
  gap: number
) {
  const rightBoundary = boundary.x + Math.max(0, boundary.width);
  let candidateY = boundary.y;
  let attempts = 0;

  while (attempts < maxScanAttempts) {
    let candidateX = boundary.x;

    while (
      attempts < maxScanAttempts &&
      (candidateX + dimensions.width <= rightBoundary || candidateX === boundary.x)
    ) {
      attempts += 1;

      const candidateRect = {
        height: dimensions.height,
        width: dimensions.width,
        x: candidateX,
        y: candidateY,
      };
      const collision = occupiedRects.find((rect) => intersects(candidateRect, rect, gap));

      if (!collision) {
        return { x: Math.round(candidateX), y: Math.round(candidateY) };
      }

      candidateX = Math.max(candidateX + gap, collision.x + collision.width + gap);
    }

    candidateY = getNextRowY(candidateY, dimensions.height, occupiedRects, gap);
  }

  return { x: Math.round(boundary.x), y: Math.round(candidateY) };
}

function getNextRowY(candidateY: number, height: number, occupiedRects: Rect[], gap: number) {
  const rowBottom = occupiedRects
    .filter((rect) => candidateY < rect.y + rect.height + gap && candidateY + height + gap > rect.y)
    .reduce((bottom, rect) => Math.max(bottom, rect.y + rect.height + gap), candidateY + gap);

  return Math.max(candidateY + gap, rowBottom);
}

function intersects(left: Rect, right: Rect, gap: number) {
  return (
    left.x < right.x + right.width + gap &&
    left.x + left.width + gap > right.x &&
    left.y < right.y + right.height + gap &&
    left.y + left.height + gap > right.y
  );
}
